"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  containerClassName?: string;
  tableClassName?: string;
  columnClassNames?: Record<string, string>;
  /** Shown in place of the default "No results." row when there is no data. */
  emptyState?: ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  containerClassName,
  tableClassName,
  columnClassNames,
  emptyState,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const tableMarkup = (
    <Table className={tableClassName}>
      <TableHeader className="sticky top-0 bg-iai-header-bg z-10 border-b">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  className={cn(
                    "text-left whitespace-nowrap overflow-hidden",
                    !columnClassNames?.[header.column.id] &&
                      header.column.id === "select" &&
                      "w-12 min-w-12 max-w-12",
                    !columnClassNames?.[header.column.id] &&
                      header.column.id === "actions" &&
                      "w-24 min-w-24 max-w-24",
                    columnClassNames?.[header.column.id]
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              className={cn(
                "group",
                onRowClick && "cursor-pointer hover:bg-muted/50"
              )}
              onClick={() => onRowClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(
                    "whitespace-nowrap overflow-hidden",
                    !columnClassNames?.[cell.column.id] &&
                      cell.column.id === "select" &&
                      "w-12 min-w-12 max-w-12",
                    !columnClassNames?.[cell.column.id] &&
                      cell.column.id === "actions" &&
                      "w-24 min-w-24 max-w-24",
                    columnClassNames?.[cell.column.id]
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              {emptyState ?? "No results."}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  if (containerClassName) {
    return (
      <div className={cn("overflow-auto", containerClassName)}>{tableMarkup}</div>
    );
  }

  return tableMarkup;
}
